// AUTO-GENERATED FILE - DO NOT EDIT
// Generated by scripts/generate-cube-meta.ts
import type {
  CubeMeasureEntry,
  CubeDimensionEntry,
  CubeJoinEntry
} from 'shared/src/api/cube-api-types';

export const userActivityDailyCubeMeta = {
  name: 'user_activity_daily',
  sqlTable: 'metrics.user_activity_daily',
  title: 'User Activity Daily',
  description:
    'Daily user activity on DSP platforms. Tracks visible and active time per user per campaign/strategy/line-item.',
  joins: {
    campaigns: { relationship: 'many_to_one' } as const satisfies CubeJoinEntry,
    line_items: { relationship: 'many_to_one' } as const satisfies CubeJoinEntry
  },
  measures: {
    'user_activity_daily.count': {
      type: 'number',
      aggType: 'count',
      title: 'Record Count',
      description: 'Number of daily activity records',
      meta: {
        category: 'utility',
        grain: 'daily',
        supports_rollup: true,
        formula: 'COUNT(*)',
        inputs: [],
        short_description: 'Row count, not a business metric',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'user_activity_daily.total_visible_ms': {
      sql: 'total_visible_ms',
      type: 'number',
      aggType: 'sum',
      title: 'Total Visible Time (ms)',
      description:
        'Total milliseconds the DSP tab was visible (user may or may not be interacting)',
      meta: {
        category: 'activity',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_visible_ms)',
        inputs: [],
        short_description: 'Total milliseconds DSP tab was visible',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'user_activity_daily.total_active_ms': {
      sql: 'total_active_ms',
      type: 'number',
      aggType: 'sum',
      title: 'Total Active Time (ms)',
      description: 'Total milliseconds the user was actively interacting with the DSP',
      meta: {
        category: 'activity',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_active_ms)',
        inputs: [],
        short_description: 'Total milliseconds user was actively interacting',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'user_activity_daily.session_count': {
      sql: 'session_count',
      type: 'number',
      aggType: 'sum',
      title: 'Session Count',
      description: 'Number of distinct sessions',
      meta: {
        category: 'activity',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(session_count)',
        inputs: [],
        short_description: 'Number of distinct sessions',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'user_activity_daily.user_count': {
      sql: 'user_id',
      type: 'number',
      aggType: 'countDistinct',
      title: 'User Count',
      description: 'Number of distinct users with activity',
      meta: {
        category: 'activity',
        grain: 'daily',
        supports_rollup: false,
        formula: 'COUNT(DISTINCT user_id)',
        inputs: [],
        short_description: 'Number of distinct users with activity',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'user_activity_daily.total_visible_minutes': {
      sql: 'total_visible_ms / 60000.0',
      type: 'number',
      aggType: 'sum',
      title: 'Total Visible Time (min)',
      description: 'Total minutes the DSP tab was visible',
      meta: {
        category: 'activity',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_visible_ms) / 60000',
        inputs: [],
        short_description: 'Total visible time in minutes',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'user_activity_daily.total_active_minutes': {
      sql: 'total_active_ms / 60000.0',
      type: 'number',
      aggType: 'sum',
      title: 'Total Active Time (min)',
      description: 'Total minutes the user was actively interacting with the DSP',
      meta: {
        category: 'activity',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_active_ms) / 60000',
        inputs: [],
        short_description: 'Total active time in minutes',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'user_activity_daily.page_view_count': {
      sql: 'page_view_count',
      type: 'number',
      aggType: 'sum',
      title: 'Page View Count',
      description: 'Number of distinct page views',
      meta: {
        category: 'activity',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(page_view_count)',
        inputs: [],
        short_description: 'Number of distinct page views',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeMeasureEntry
  },
  dimensions: {
    'user_activity_daily.id': {
      sql: "user_activity_daily.user_id || '-' || user_activity_daily.campaign_id || '-' || COALESCE(user_activity_daily.line_item_id::text, '') || '-' || user_activity_daily.day::text",
      type: 'string',
      title: 'Id',
      description: 'Composite key: user_id + campaign_id + line_item_id + day',
      primaryKey: true,
      meta: {
        short_description: 'Composite key uniquely identifying each daily user activity row.',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'user_activity_daily.user_id': {
      sql: 'user_id',
      type: 'string',
      title: 'User ID',
      description: 'Bravo user identifier',
      meta: {
        short_description: 'Bravo user identifier',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'user_activity_daily.campaign_id': {
      sql: 'campaign_id',
      type: 'string',
      title: 'Campaign ID',
      description: 'Resolved campaign identifier',
      meta: {
        short_description: 'Campaign resolved from platform entity',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'user_activity_daily.strategy_id': {
      sql: 'strategy_id',
      type: 'string',
      title: 'Strategy ID',
      description: 'Resolved strategy identifier',
      meta: {
        short_description: 'Strategy resolved from platform entity',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'user_activity_daily.line_item_id': {
      sql: 'line_item_id',
      type: 'string',
      title: 'Line Item ID',
      description: 'Resolved line item identifier',
      meta: {
        short_description: 'Line item resolved from platform entity',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'user_activity_daily.media_platform_id': {
      sql: 'media_platform_id',
      type: 'string',
      title: 'Media Platform ID',
      description:
        'Platform ID resolved from media_platform_entity_id via platform_entity_resolution',
      meta: {
        short_description: 'Platform ID from the resolution view',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'user_activity_daily.media_platform_entity_id': {
      sql: 'media_platform_entity_id',
      type: 'string',
      title: 'Media Platform Entity ID',
      description: 'Platform entity identifier from the telemetry event',
      meta: {
        short_description: 'Platform entity from the telemetry event',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'user_activity_daily.platform_name': {
      sql: 'platform_name',
      type: 'string',
      title: 'Platform Name',
      description: 'Name of the DSP platform (e.g. DV360, The Trade Desk)',
      meta: {
        short_description: 'DSP platform name resolved from media_platform_id',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'user_activity_daily.day': {
      sql: 'day',
      type: 'time',
      title: 'Day',
      description: 'Calendar date for this activity record',
      meta: {
        short_description: 'Calendar date for daily activity record',
        source_tables: ['metrics.user_activity_daily']
      }
    } as const satisfies CubeDimensionEntry
  }
};
